import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import Textarea from '../ui/Textarea';

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (comment: string) => void;
  timeLogged?: string;
  taskName: string;
}

export function CommentModal({ isOpen, onClose, onSave, timeLogged, taskName }: CommentModalProps) {
  const [comment, setComment] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const tag = (e.target as HTMLElement).tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!isOpen) return null;

  // Use a portal so this modal renders at document.body — completely outside any
  // parent <form> elements. Without this, when CommentModal is used inside TaskModal
  // (which has its own <form>), the browser "fixes" the invalid nested-form HTML by
  // associating the Save button with the outer form instead, causing TaskModal to
  // submit and close before the comment is ever saved.

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (comment.trim()) {
      onSave(comment.trim());
      setComment('');
    }
  };

  const handleSkip = () => {
    onClose();
    setComment('');
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-gray-800 border border-gray-700 rounded-panel shadow-overlay w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-100">
              Agregar comentario
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {taskName}
            </p>
          </div>
          <IconButton
            icon={X}
            aria-label="Cerrar"
            variant="ghost"
            onClick={handleSkip}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {timeLogged && (
              <div className="bg-brand-500/20 border border-brand-500/30 rounded-panel p-3">
                <p className="text-sm text-brand-300">
                  <span className="font-medium">Tiempo registrado:</span> {timeLogged}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ¿Qué avances has realizado en esta tarea?
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Describe los avances, problemas encontrados, o siguientes pasos..."
                onKeyDown={(e) => e.stopPropagation()}
                rows={6}
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 bg-gray-900/50 rounded-b-panel border-t border-gray-700">
            <Button
              type="button"
              variant="secondary"
              onClick={handleSkip}
            >
              Omitir
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!comment.trim()}
            >
              Guardar comentario
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
